import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { mitErsatz } from "@/office/makro/pruefung";
import { readDocumentSettings } from "@/office/word/dokument";
import { distinctTypes, readContentControls } from "@/office/word/formulare";
import { MAKRO_DOKUMENT } from "../makros";

export const lektionFormularSchuetzen: Lesson = {
  id: "formular-schuetzen",
  title: "Das Formular schützen und abschließen",
  summary: "Nur die Felder freigeben – und den Rest vor versehentlichen Änderungen sichern.",
  durationMinutes: 9,
  learningGoals: [
    "Ein Formular gegen Änderungen schützen",
    "Die Bearbeitung auf Formularfelder beschränken",
    "Ein Formular als Vorlage weitergeben",
  ],
  steps: [
    {
      id: "bearbeitung-einschraenken",
      title: "Die Bearbeitung einschränken",
      explanation: [
        "Ein ungeschütztes Formular hält selten lange: Beim Ausfüllen gerät die " +
          "Beschriftung durcheinander, ein Feld wird versehentlich gelöscht, jemand " +
          "schreibt in die Erklärung statt ins Feld.",
        "Word löst das über „Entwicklertools“ → „Bearbeitung einschränken“. Rechts " +
          "öffnet sich ein Bereich. Dort setzt du unter Punkt 2 den Haken bei " +
          "„Bearbeitung auf diese Formatierungsart im Dokument einschränken“ und wählst " +
          "in der Liste „Ausfüllen von Formularen“.",
        "Mit „Ja, Schutz jetzt anwenden“ wird der Schutz aktiv. Word fragt optional nach " +
          "einem Kennwort – das schützt vor versehentlichem Aufheben, ist aber kein " +
          "ernsthafter Zugriffsschutz. Merke es dir gut, denn Word bietet keine " +
          "Wiederherstellung an.",
        "Danach lässt sich nur noch in die Steuerelemente schreiben. Mit Tab springt die " +
          "ausfüllende Person von Feld zu Feld – das eigentliche Komfortmerkmal eines " +
          "richtigen Formulars.",
      ],
      keyPoints: [
        "Entwicklertools → Bearbeitung einschränken → „Ausfüllen von Formularen“.",
        "Im geschützten Zustand springt Tab von Feld zu Feld.",
        "Das Kennwort lässt sich nicht wiederherstellen.",
      ],
      task: {
        instruction:
          "Dein Formular soll mindestens vier Steuerelemente mit mindestens drei " +
          "verschiedenen Typen enthalten, bevor du es schützt. Schütze es anschließend " +
          "über „Ausfüllen von Formularen“.",
        hint: "Ist der Schutz schon aktiv, hebe ihn kurz auf, falls noch Felder fehlen.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: async () => {
          const felder = await readContentControls();
          if (felder.length === 0) {
            return fail(
              "Es wurde noch kein Inhaltssteuerelement gefunden.",
              "Ist der Schutz bereits aktiv, hebe ihn kurz auf, um die Felder zu ergänzen.",
            );
          }

          const typen = distinctTypes(felder);
          const mitTag = felder.filter((feld) => feld.tag.length > 0);

          const aufbau = [
            criterion(`Mindestens vier Steuerelemente (gefunden: ${felder.length})`, felder.length >= 4),
            criterion(`Mindestens drei verschiedene Typen (gefunden: ${typen.join(", ")})`, typen.length >= 3),
            criterion(
              `Mindestens die Hälfte der Felder hat ein Tag (${mitTag.length} von ${felder.length})`,
              felder.length > 0 && mitTag.length * 2 >= felder.length,
            ),
          ];

          return mitErsatz(
            async () => (await readDocumentSettings())?.protectionType ?? null,
            MAKRO_DOKUMENT,
            // VBA: wdAllowOnlyFormFields = 2, wdNoProtection = -1.
            (bericht) => (bericht.zahl("schutz") === 2 ? "AllowOnlyFormFields" : String(bericht.zahl("schutz") ?? "")),
            (schutz) =>
              fromCriteria(
                [...aufbau, criterion("Das Dokument ist auf „Ausfüllen von Formularen“ beschränkt", schutz === "AllowOnlyFormFields")],
                {
                  success: "Das Formular ist vollständig und geschützt – außerhalb der Felder lässt sich nichts mehr ändern.",
                  failure: "Am Formular fehlt noch etwas:",
                  hint: "Tags vergibst du über Entwicklertools → Eigenschaften. Den Schutz aktivierst du über Entwicklertools → Bearbeitung einschränken.",
                },
              ),
          );
        },
      },
    },
    {
      id: "weitergeben",
      title: "Als Vorlage weitergeben",
      explanation: [
        "Ein Formular, das mehrfach ausgefüllt werden soll, gibt man nicht als normales " +
          "Dokument weiter. Sonst speichert die erste Person ihre Eingaben in die " +
          "Originaldatei, und die nächste findet ein bereits ausgefülltes Formular vor.",
        "Der richtige Weg ist die Dokumentvorlage: „Datei“ → „Speichern unter“, als " +
          "Dateityp „Word-Vorlage (*.dotx)“ wählen. Ein Doppelklick auf eine Vorlage " +
          "öffnet immer eine neue, leere Kopie – das Original bleibt unberührt.",
        "Mehr zu Vorlagen im nächsten Modul. Für Formulare ist es die einzige sinnvolle " +
          "Art der Weitergabe.",
      ],
      keyPoints: [
        "Speichern unter → Dateityp „Word-Vorlage (*.dotx)“.",
        "Eine Vorlage öffnet immer eine neue Kopie.",
        "Ein als .docx verteiltes Formular wird beim ersten Ausfüllen überschrieben.",
      ],
    },
  ],
};
